import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, FormControlLabel,
  IconButton, Chip, CircularProgress, Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import CatalogShortNameField from '../../components/CatalogShortNameField';
import { generateCatalogShortName } from '../../utils/catalogShortName';

const emptyForm = {
  name: '', code: '', short_name: '', description: '', version: '', category: 'Application',
  features: '', is_active: true, show_on_signup: true, sort_order: 0
};

const SoftwareApplicationsSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editShortName, setEditShortName] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catalog/applications');
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditShortName(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      code: row.code,
      short_name: row.short_name || generateCatalogShortName(row.name),
      description: row.description || '',
      version: row.version || '',
      category: row.category || 'Application',
      features: Array.isArray(row.features) ? row.features.join(', ') : '',
      is_active: row.is_active,
      show_on_signup: row.show_on_signup,
      sort_order: row.sort_order || 0
    });
    setEditShortName(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      features: form.features.split(',').map((s) => s.trim()).filter(Boolean)
    };
    try {
      if (editing) {
        await api.put(`/catalog/applications/${editing.id}`, payload);
        toast.success('Application updated');
      } else {
        await api.post('/catalog/applications', payload);
        toast.success('Application created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/catalog/applications/${row.id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Software Applications</Typography>
          <Typography variant="body2" color="text.secondary">
            Register products shown on the Tenant Signup page.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Application</Button>}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Applications marked &quot;Show on signup&quot; appear on the public Tenant Signup page for customers to choose.
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Shortname</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Signup</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography fontWeight={600}>{row.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                </TableCell>
                <TableCell><code>{row.short_name || '—'}</code></TableCell>
                <TableCell><code>{row.code}</code></TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.is_active ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" label="No" />}</TableCell>
                <TableCell>{row.show_on_signup ? 'Visible' : 'Hidden'}</TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <>
                      <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={7} align="center">No applications yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Application' : 'Add Application'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            {!editing && <TextField label="Code (optional)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} fullWidth />}
            <CatalogShortNameField
              name={form.name}
              shortName={form.short_name}
              onShortNameChange={(short_name) => setForm((prev) => ({ ...prev, short_name }))}
              editEnabled={editShortName}
              onEditEnabledChange={setEditShortName}
            />
            <TextField label="Description" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <TextField label="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} fullWidth />
            <TextField label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} fullWidth />
            <TextField label="Features (comma-separated)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} fullWidth />
            <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
            <FormControlLabel control={<Switch checked={form.show_on_signup} onChange={(e) => setForm({ ...form, show_on_signup: e.target.checked })} />} label="Show on Tenant Signup" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoftwareApplicationsSettings;
